import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import styles from "./ProjectedORChart.module.css";
import RoundedBackground from "../RoundedBackgroundCharts";

const MyTooltipComponent = ({ payload }) => {
  const data = payload?.[0]?.payload;
  if (!data) return null;

  const color = data.dataType === "projected" ? "#00B2FF" : "#FFEC9A";

  return (
    <div className={styles.tooltip}>
      <div className={styles.tooltipItem}>
        <span className={styles.dot} style={{ backgroundColor: "#6C22B5" }} />
        <span>Plan: {data.plan}</span>
      </div>
      <div className={styles.tooltipItem}>
        <span className={styles.dot} style={{ backgroundColor: color }} />
        <span>{data.dataType}: {data.actOrProj}</span>
      </div>
    </div>
  );
};

export default function ProjectedORChart({ data, minWidth = 40, showActual = false }) {
  const processedData = data.map((d) => ({
    day: d.day,
    plan: d.plan,
    actOrProj: d.actual !== 0 ? d.actual : d.projected,
    dataType: d.actual !== 0 ? "actual" : "projected",
  }));

  const chartWidth = processedData.length * minWidth;

  return (
    <div className={styles.chartWrapper}>
      <div className={styles.chartInner} style={{ minWidth: chartWidth }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={processedData}
            margin={{ top: 20, right: 10, bottom: 30, left: showActual ? 10 : 0 }}
            barGap={4}
            barCategoryGap={8}
          >
            <defs>
              <linearGradient id="planGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#C79CD5" />
                <stop offset="100%" stopColor="#6C22B5" />
              </linearGradient>
              <linearGradient id="actualGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#FFEC9A" />
                <stop offset="100%" stopColor="#00B2FF" />
              </linearGradient>
              <linearGradient id="projectedGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#00B2FF" />
                <stop offset="100%" stopColor="#0070D8" />
              </linearGradient>
            </defs>

            <XAxis
              dataKey="day"
              height={35}
              axisLine={false}
              tickLine={false}
              padding={{ left: 10, right: 10 }}
              tick={({ x, y, payload }) => {
                const isToday = payload.value === new Date().toLocaleString("en-US", { weekday: "short" });
                return (
                  <g transform={`translate(${x},${y})`}>
                    <text
                      x={0}
                      y={0}
                      dy={16}
                      textAnchor="middle"
                      fill={isToday ? "#00B2FF" : "#8A93A6"}
                      fontSize={12}
                    >
                      {payload.value}
                    </text>
                    {isToday && <circle cx={0} cy={26} r={3} fill="#00B2FF" />}
                  </g>
                );
              }}
            />

            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fill: "#8A93A6", fontSize: 9 }}
              width={35}
              interval={0}
            />

            <Tooltip content={<MyTooltipComponent />} />

            <Bar
              dataKey="plan"
              radius={[8, 8, 8, 8]}
              fill="url(#planGradient)"
              barSize={12}
              background={<RoundedBackground />}
            />
            <Bar
              dataKey="actOrProj"
              radius={[8, 8, 8, 8]}
              barSize={12}
              background={<RoundedBackground />}
              shape={({ x, y, width, height, index }) => {
                const gradientId = processedData[index].dataType === "actual" ? "actualGradient" : "projectedGradient";
                return (
                  <rect
                    x={x}
                    y={y}
                    width={width}
                    height={height}
                    rx={8}
                    ry={8}
                    fill={`url(#${gradientId})`}
                  />
                );
              }}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className={styles.legend}>
        <div className={styles.legendItem}>
          <span className={styles.square} style={{ background: "linear-gradient(180deg, #C79CD5 0%, #6C22B5 100%)" }} />
          <span>Plan</span>
        </div>
        <div className={styles.legendItem}>
          <span className={styles.square} style={{ background: "linear-gradient(180deg, #FFEC9A 0%, #00B2FF 100%)" }} />
          <span>Actual</span>
        </div>
        {!showActual && (
          <div className={styles.legendItem}>
            <span className={styles.square} style={{ backgroundColor: "#4DA1FF" }} />
            <span>Projected</span>
          </div>
        )}
      </div>
    </div>
  );
}
